import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/auth.store";
import { useOrganizationStore } from "../../stores/organization.store";
import {
  clearOnboardingLaterDismissed,
  getOnboardingIsNew,
  isOnboardingLaterDismissed,
  markOnboardingLaterDismissed,
} from "../../utils/selfSetupStorage";
import {
  activateOnboardingResume,
  finishSelfSetupTour,
  getActiveSelfSetupTourStep,
  getCompletedSelfSetupSteps,
  getFirstIncompleteSelfSetupStep,
  getNextTourPhase,
  getSelfSetupTourOrder,
  getSelfSetupTourPath,
  getSelfSetupTourPhase,
  isOnboardingCongratsPending,
  markSelfSetupStepCompleted,
  parkActiveOnboardingOnLogout,
  pauseOnboardingAtHub,
  pauseOnboardingAtTip,
  recoverOrphanLiveOnboardingTip,
  setActiveSelfSetupTourStep,
  setSelfSetupTourPhase,
  type SelfSetupTourPhase,
  type SelfSetupTourStep,
} from "../../utils/selfSetupTour";

type SelfSetupTourContextValue = {
  applies: boolean;
  /** True while onboarding is unfinished (`isNew` in localStorage). */
  isNew: boolean;
  activeStep: SelfSetupTourStep | null;
  tourPhase: SelfSetupTourPhase;
  showWelcomeHub: boolean;
  showCongrats: boolean;
  steps: { key: SelfSetupTourStep; done: boolean }[];
  nextIncomplete: SelfSetupTourStep | null;
  allDone: boolean;
  startTour: () => void;
  nextStep: () => void;
  completeStep: (step: SelfSetupTourStep) => void;
  completeSteps: (steps: SelfSetupTourStep[]) => void;
  dismissTour: () => void;
  dismissCongrats: () => void;
  goToStep: (step: SelfSetupTourStep) => void;
  isStep: (step: SelfSetupTourStep) => boolean;
  setTourPhase: (phase: SelfSetupTourPhase) => void;
  advanceTourPhase: () => void;
  handleNavTourClick: (step: SelfSetupTourStep) => void;
};

const SelfSetupTourContext = createContext<SelfSetupTourContextValue | null>(
  null,
);

function readIsNew(userId: string, orgId: string): boolean {
  return getOnboardingIsNew(userId, orgId);
}

export function SelfSetupTourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id ?? null;
  const orgType = selectedOrganization?.organization_type ?? null;
  const userId = user?.id ?? user?.sub ?? "";
  const tourOrder = useMemo(
    () => getSelfSetupTourOrder(orgType),
    [orgType],
  );

  /** Welcome / steps / congrats: any signed-in user with an org; gated only by isNew. */
  const eligible = Boolean(orgId && userId);

  const [isNew, setIsNew] = useState(() => {
    if (!userId || !orgId) return false;
    return readIsNew(userId, orgId);
  });

  const [activeStep, setActiveStep] = useState<SelfSetupTourStep | null>(() => {
    if (!userId || !orgId) return null;
    if (!readIsNew(userId, orgId)) return null;
    recoverOrphanLiveOnboardingTip(userId, orgId);
    return getActiveSelfSetupTourStep(userId, orgId);
  });

  const [tourPhase, setTourPhaseState] = useState<SelfSetupTourPhase>(() => {
    if (!userId || !orgId) return "nav";
    return getSelfSetupTourPhase(userId, orgId);
  });

  const [completedSteps, setCompletedSteps] = useState<SelfSetupTourStep[]>(
    () => {
      if (!userId || !orgId) return [];
      return getCompletedSelfSetupSteps(userId, orgId, orgType);
    },
  );

  const [showCongrats, setShowCongrats] = useState(() => {
    if (!userId || !orgId || !readIsNew(userId, orgId)) return false;
    return isOnboardingCongratsPending(userId, orgId);
  });

  /**
   * Soft-hide after Later/X — sessionStorage (survives Refresh).
   * Cleared on logout so welcome returns after re-login.
   */
  const [laterDismissed, setLaterDismissed] = useState(() => {
    if (!userId || !orgId) return false;
    return isOnboardingLaterDismissed(userId, orgId);
  });

  const syncFromStorage = useCallback(() => {
    if (!eligible || !userId || !orgId) {
      setIsNew(false);
      setActiveStep(null);
      setTourPhaseState("nav");
      setCompletedSteps([]);
      setShowCongrats(false);
      setLaterDismissed(false);
      return;
    }

    const nextIsNew = readIsNew(userId, orgId);
    setIsNew(nextIsNew);
    setLaterDismissed(isOnboardingLaterDismissed(userId, orgId));

    if (!nextIsNew) {
      setActiveStep(null);
      setTourPhaseState("nav");
      setCompletedSteps([]);
      setShowCongrats(false);
      return;
    }

    // Orphan live tip (logout missed park) → park as resume so welcome shows.
    recoverOrphanLiveOnboardingTip(userId, orgId);

    const step = getActiveSelfSetupTourStep(userId, orgId);
    setActiveStep(step);
    setTourPhaseState(getSelfSetupTourPhase(userId, orgId, step));
    setCompletedSteps(getCompletedSelfSetupSteps(userId, orgId, orgType));
    setShowCongrats(isOnboardingCongratsPending(userId, orgId));
  }, [eligible, userId, orgId, orgType]);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage]);

  // On logout (ids drop): park live tip as resume + clear Later soft-hide
  // so welcome returns after re-login (backup if auth.store park missed org).
  const prevAuthIds = useRef<{ userId: string; orgId: string } | null>(null);
  useEffect(() => {
    if (userId && orgId) {
      prevAuthIds.current = { userId, orgId };
      return;
    }
    if (prevAuthIds.current) {
      const { userId: uid, orgId: oid } = prevAuthIds.current;
      parkActiveOnboardingOnLogout(uid, oid);
      clearOnboardingLaterDismissed(uid, oid);
      prevAuthIds.current = null;
      setLaterDismissed(false);
    }
  }, [userId, orgId]);

  const applies = Boolean(eligible && isNew);

  const nextIncomplete = useMemo(
    () => getFirstIncompleteSelfSetupStep(completedSteps, orgType),
    [completedSteps, orgType],
  );

  const allDone = nextIncomplete === null && completedSteps.length > 0;

  // Congrats whenever every step is done and onboarding is still open.
  const congratsOpen = Boolean(
    applies && allDone && !activeStep && !laterDismissed,
  );
  const effectiveShowCongrats = (showCongrats || congratsOpen) && !laterDismissed;

  const showWelcomeHub = Boolean(
    applies &&
      !activeStep &&
      !effectiveShowCongrats &&
      !allDone &&
      !laterDismissed,
  );

  const tourApplies = Boolean(
    applies && !laterDismissed && (activeStep || showWelcomeHub),
  );

  const steps = useMemo(
    () =>
      tourOrder.map((key: SelfSetupTourStep) => ({
        key,
        done: completedSteps.includes(key),
      })),
    [completedSteps, tourOrder],
  );

  const setTourPhase = useCallback(
    (phase: SelfSetupTourPhase) => {
      if (!userId || !orgId) return;
      setSelfSetupTourPhase(userId, orgId, phase);
      setTourPhaseState(phase);
    },
    [userId, orgId],
  );

  const advanceTourPhase = useCallback(() => {
    if (!userId || !orgId || !activeStep) return;
    const next = getNextTourPhase(activeStep, tourPhase);
    if (!next) return;
    setTourPhase(next);
  }, [userId, orgId, activeStep, tourPhase, setTourPhase]);

  const goToStep = useCallback(
    (step: SelfSetupTourStep) => {
      if (!userId || !orgId) return;
      clearOnboardingLaterDismissed(userId, orgId);
      setLaterDismissed(false);
      setActiveSelfSetupTourStep(userId, orgId, step);
      setActiveStep(step);
      setTourPhaseState("nav");
      setShowCongrats(false);
    },
    [userId, orgId],
  );

  const handleNavTourClick = useCallback(
    (step: SelfSetupTourStep) => {
      if (!userId || !orgId) return;
      if (activeStep !== step || tourPhase !== "nav") {
        navigate(getSelfSetupTourPath(step, orgId).path);
        return;
      }

      if (step === "report") {
        setTourPhase("period");
        navigate("/report");
        return;
      }

      if (step === "branchAnalysis") {
        setTourPhase("monthSelect");
        navigate("/branch-analysis");
        return;
      }

      setTourPhase("monthlyTab");
      navigate("/");
    },
    [userId, orgId, activeStep, tourPhase, navigate, setTourPhase],
  );

  const dismissTour = useCallback(() => {
    if (!userId || !orgId) return;
    // Later: keep isNew + resume tip; hide until logout / re-login.
    if (activeStep) {
      pauseOnboardingAtTip(userId, orgId, activeStep, tourPhase);
    } else {
      pauseOnboardingAtHub(userId, orgId);
    }
    markOnboardingLaterDismissed(userId, orgId);
    setLaterDismissed(true);
    setActiveStep(null);
    setTourPhaseState("nav");
    setShowCongrats(false);
  }, [userId, orgId, activeStep, tourPhase]);

  const dismissCongrats = useCallback(() => {
    setShowCongrats(false);
    if (!userId || !orgId) return;
    finishSelfSetupTour(userId, orgId);
    setIsNew(false);
    setLaterDismissed(false);
    setCompletedSteps([]);
    setActiveStep(null);
    setTourPhaseState("nav");
  }, [userId, orgId]);

  const startTour = useCallback(() => {
    if (!applies || !userId || !orgId) return;
    clearOnboardingLaterDismissed(userId, orgId);
    setLaterDismissed(false);

    // Continue from the last saved tip (page + spot), if any.
    const resumed = activateOnboardingResume(userId, orgId);
    if (resumed) {
      setActiveStep(resumed.page);
      setTourPhaseState(resumed.spot);
      setShowCongrats(false);
      const target = getSelfSetupTourPath(resumed.page, orgId);
      navigate(target.path, { state: target.state });
      return;
    }

    const completed = getCompletedSelfSetupSteps(userId, orgId, orgType);
    const target = getFirstIncompleteSelfSetupStep(completed, orgType);
    if (!target) {
      setActiveStep(null);
      setTourPhaseState("nav");
      setShowCongrats(true);
      navigate("/");
      return;
    }
    goToStep(target);
  }, [applies, userId, orgId, orgType, goToStep, navigate]);

  const completeStep = useCallback(
    (step: SelfSetupTourStep) => {
      if (!userId || !orgId) return;
      clearOnboardingLaterDismissed(userId, orgId);
      setLaterDismissed(false);
      const updated = markSelfSetupStepCompleted(userId, orgId, step, orgType);
      setCompletedSteps(updated);
      setActiveStep(null);
      setTourPhaseState("nav");
      const stillIncomplete = getFirstIncompleteSelfSetupStep(updated, orgType);
      if (stillIncomplete) {
        setShowCongrats(false);
      } else {
        setShowCongrats(true);
      }
      navigate("/");
    },
    [userId, orgId, orgType, navigate],
  );

  const completeSteps = useCallback(
    (stepsToComplete: SelfSetupTourStep[]) => {
      if (!userId || !orgId || stepsToComplete.length === 0) return;
      clearOnboardingLaterDismissed(userId, orgId);
      setLaterDismissed(false);
      let updated = getCompletedSelfSetupSteps(userId, orgId, orgType);
      for (const s of stepsToComplete) {
        updated = markSelfSetupStepCompleted(userId, orgId, s, orgType);
      }
      setCompletedSteps(updated);
      setActiveStep(null);
      setTourPhaseState("nav");
      const stillIncomplete = getFirstIncompleteSelfSetupStep(updated, orgType);
      setShowCongrats(!stillIncomplete);
      navigate("/");
    },
    [userId, orgId, orgType, navigate],
  );

  const nextStep = useCallback(() => {
    if (!activeStep) return;
    completeStep(activeStep);
  }, [activeStep, completeStep]);

  const isStep = useCallback(
    (step: SelfSetupTourStep) => Boolean(tourApplies && activeStep === step),
    [tourApplies, activeStep],
  );

  const value = useMemo(
    () => ({
      applies,
      isNew,
      activeStep,
      tourPhase,
      showWelcomeHub,
      showCongrats: effectiveShowCongrats,
      steps,
      nextIncomplete,
      allDone,
      startTour,
      nextStep,
      completeStep,
      completeSteps,
      dismissTour,
      dismissCongrats,
      goToStep,
      isStep,
      setTourPhase,
      advanceTourPhase,
      handleNavTourClick,
    }),
    [
      applies,
      isNew,
      activeStep,
      tourPhase,
      showWelcomeHub,
      effectiveShowCongrats,
      steps,
      nextIncomplete,
      allDone,
      startTour,
      nextStep,
      completeStep,
      completeSteps,
      dismissTour,
      dismissCongrats,
      goToStep,
      isStep,
      setTourPhase,
      advanceTourPhase,
      handleNavTourClick,
    ],
  );

  return (
    <SelfSetupTourContext.Provider value={value}>
      {children}
    </SelfSetupTourContext.Provider>
  );
}

export function useSelfSetupTour(): SelfSetupTourContextValue {
  const ctx = useContext(SelfSetupTourContext);
  if (!ctx) {
    throw new Error("useSelfSetupTour must be used within SelfSetupTourProvider");
  }
  return ctx;
}
